import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCookieConsentContext } from "@/hooks/useCookieConsent";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const CookieBanner = () => {
  const { hasUserConsented, isLoading, acceptAll, rejectAll, setConsent } = useCookieConsentContext();
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Don't show if loading or user already consented
  if (isLoading || hasUserConsented) {
    return null;
  }

  const handleSaveCustom = () => {
    setConsent({
      analytics: analyticsEnabled,
      functional: true, // Always true since Mapbox/reCAPTCHA are now essential
    });
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 md:p-4 animate-slide-up">
      <div className="container max-w-3xl mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-lg p-4 md:p-5">
          {!showCustomize ? (
            // Main banner - compact version
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Nous utilisons des cookies essentiels et analytiques optionnels.{" "}
                  <Link to="/politique-confidentialite" className="text-primary hover:underline">
                    En savoir plus
                  </Link>
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-end shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCustomize(true)}
                  className="text-xs h-8"
                >
                  Personnaliser
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectAll}
                  className="text-xs h-8"
                >
                  Refuser
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="bg-primary hover:bg-primary/90 text-xs h-8"
                >
                  Accepter
                </Button>
              </div>
            </div>
          ) : (
            // Customize panel - compact version
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-foreground">Préférences cookies</h4>
                <Button variant="ghost" size="sm" onClick={() => setShowCustomize(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1 pr-3">
                    <p className="text-sm font-medium">Essentiels</p>
                    <p className="text-xs text-muted-foreground">Fonctionnement du site</p>
                  </div>
                  <Switch checked={true} disabled />
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1 pr-3">
                    <p className="text-sm font-medium">Analytiques</p>
                    <p className="text-xs text-muted-foreground">Matomo (hébergé en Europe)</p>
                  </div>
                  <Switch checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={rejectAll} className="text-xs h-8">
                  Refuser
                </Button>
                <Button size="sm" onClick={handleSaveCustom} className="bg-primary hover:bg-primary/90 text-xs h-8">
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
